import React, { useEffect, useState, useRef } from "react";
import { defineBlock, useSmartProp, smartProps, EditableHeading, EditableImg, useInspectorControl, useEditor } from "@smartfire/gutenberg-react";
import { PanelBody, PanelRow, TextControl, ToggleControl } from "@wordpress/components";
import { MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import Button from "../button/block.js";
import apiFetch from '@wordpress/api-fetch';
import Item from "./item/index.js";
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import SearchIcon from '@material-ui/icons/Search';
import style from "./block.module.scss";
import clsx from "clsx";

function Inspector() {
    const [numberPosts, setNumberPosts] = useSmartProp('numberPosts');
    const [isSearchBar, setIsSearchBar] = useSmartProp('isSearchBar');
    const [isButton, setIsButton] = useSmartProp('isButton');
    const [isBlank, setIsBlank] = useSmartProp('isBlank');
    const [urlButton, setUrlButton] = useSmartProp('urlButton');

    return (
        <>
            <PanelBody title="Paramètres" initialOpen={true}>
                <PanelRow>
                    <TextControl
                        label="Nombre d'articles"
                        value={ numberPosts }
                        onChange={(numberPosts) => setNumberPosts(numberPosts)}
                        type="number"
                    />
                </PanelRow>
                <PanelRow>
                    <ToggleControl
                        label={"Afficher la barre de recherche"}
                        checked={ isSearchBar }
                        onChange={() => setIsSearchBar(!isSearchBar)}
                    />
                </PanelRow>
            </PanelBody>
            <PanelBody title="Image de fond">
                <PanelRow>
                    <MediaUploadCheck>
                        <MediaUpload
                            allowedTypes={['image']}
                            render={() => (
                                <div>
                                    <EditableImg smartProp="background" />
                                </div>
                            )}
                        />
                    </MediaUploadCheck>
                </PanelRow>
            </PanelBody>
            <PanelBody title="Bouton" initialOpen={true}>
                <PanelRow>
                    <ToggleControl
                        label={"Afficher bouton \"Voir toute l'actualité\""}
                        checked={ isButton }
                        onChange={() => setIsButton(!isButton)}
                    />
                </PanelRow>
                {isButton &&
                    <>
                        <PanelRow>
                            <TextControl
                                label="Lien"
                                value={ urlButton }
                                onChange={(urlButton) => setUrlButton(urlButton)}
                            />
                        </PanelRow>
                        <PanelRow>
                            <ToggleControl
                                label={"Ouvrir dans un nouvel onglet"}
                                checked={ isBlank }
                                onChange={() => setIsBlank(!isBlank)}
                            />
                        </PanelRow>
                    </>
                }
            </PanelBody>
        </>
    );
}

function Block({isSearchBar, numberPosts, isButton, urlButton, isBlank, title, background, seeMorePosts = null}) {
    useInspectorControl(Inspector);

    const { isEditing } = useEditor();
    const [posts, setPosts] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchText, setSearchText] = useState('');
    const [total, setTotal] = useState(null);
    const [totalPages, setTotalPages] = useState(null);
    const [page, setPage] = useState(1);
    const searchInput = useRef(null);

    numberPosts = numberPosts < 1 ? '9' : numberPosts;

    useEffect(() => {
        const params = {
            _embed: 1,
            per_page: numberPosts
        };

        searchText && (params.search = searchText.toLowerCase());
        params.page = page ? page : 1;
        
        const path = '/wp/v2/posts?' + new URLSearchParams(params).toString();

        if (!seeMorePosts) {
            apiFetch({
                path: path,
                parse: false
            }).then((response) => {
                setLoading(false);
                setTotal(response.headers.get('X-WP-Total'));
                setTotalPages(response.headers.get('X-WP-TotalPages'));
                response.json().then((data) => {
                    setPosts(page === 1 ? data : prevPosts => ([...prevPosts, ...data]));
                });
            });
        }
    }, [seeMorePosts, loading, numberPosts, page, setPosts, total, totalPages, searchText]);

    const linkProps = urlButton && {
        buttonUrl: urlButton,
        blank: isBlank
    };

    const searchClick = (e) => {
        setPage(1);
        setSearchText(searchInputValue);
    }

    const seeMoreClick = (e) => {
        e.preventDefault();
        setPage(page + 1);
    }

    return (
        <div className={clsx(background && style.background, style.block)} style={{backgroundImage: background ? 'url(' + background.url : null}}>
            <div className={style.blockWrapper}>
                {(isEditing || title.content) && <EditableHeading smartProp={"title"} placeholder={"Insérer un titre..."} className={style.title} />}
                {isSearchBar &&
                    <div className={style.search}>
                        <div className={style.searchInput}>
                            <input
                                className={style.searchBar}
                                placeholder={'Rechercher'}
                                type="text"
                                onChange={(e) => setSearchInputValue(e.target.value)}
                                ref={searchInput}
                            />
                            <SearchIcon className={style.icon} />
                        </div>
                        <Button className={style.searchButton} buttonText={"Rechercher"} buttonStyle={"fill"} external={true} onClick={searchClick}/>
                    </div>
                }
                {(posts.length > 0 || seeMorePosts) ? (
                    <div className={style.itemWrapper}>
                        {(seeMorePosts ? seeMorePosts : Object.entries(posts)).map((post, index) => (
                            <div key={index}>
                                <Item key={index} post={!seeMorePosts ? post[1] : post} />
                            </div>
                        ))}
                    </div>
                ) : (
                    !loading && searchText && <p style={{textAlign: 'center'}}>Aucun résulat trouvé. Veuillez réessayer.</p>
                )}
                {isButton && (
                    <Button
                        buttonText={"Voir toute l'actualité"}
                        buttonStyle={"fill"}
                        buttonPosition={"center"}
                        external={true}
                        picto={<ArrowForwardIcon />}
                        {...linkProps}
                        className={style.button}
                    />
                )}
                {numberPosts >= 9 && total > posts.length && page < totalPages &&
                    <Button
                        className={style.seeMoreButton}
                        onClick={seeMoreClick}
                        buttonStyle={"ghost"}
                        buttonText={"Voir plus"}
                        buttonPosition={"center"}
                        external={true}
                    />
                }
            </div>
        </div>
    )
}

export default defineBlock({
    Block,
    smartProps: {
        numberPosts: smartProps.string.withDefault('3'),
        isSearchBar: smartProps.bool.withDefault(false),
        title: smartProps.object.withDefault({
            headingLevel: 2,
            content: 'L\'actu'
        }),
        isButton: smartProps.bool.withDefault(false),
        urlButton: smartProps.string.withDefault(''),
        isBlank: smartProps.bool.withDefault(false),
        background: smartProps.image
    },
    setup: {
        title: 'Listing Articles',
    }
});
